import { useQuery } from "@tanstack/react-query";
import api from "../../../../lib/network/api";

// ============================================================================
// Types & Interfaces
// ============================================================================

export interface UserStats {
  total: number;
  active: number;
  suspended: number;
  terminated: number;
  newThisMonth: number;
  newThisWeek: number;
  profileCompletionRate: number;
}

export interface ProjectStats {
  total: number;
  active: number;
  inactive: number;
  totalSlots: number;
  filledSlots: number;
  availableSlots: number;
  utilizationRate: number;
}

export interface AssignmentStats {
  total: number;
  pending: number;
  approved: number;
  active: number;
  completed: number;
  rejected: number;
  terminated: number;
}

export interface ApplicationStats {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
  approvalRate: number;
  avgResponseTime: number;
}

export interface RevenueStats {
  totalEstimatedValue: number;
  activeProjectsValue: number;
  completedProjectsValue: number;
  avgHourlyRate: number;
}

export interface RecentActivity {
  _id: string;
  type: string;
  title: string;
  description: string;
  timestamp: string;
  metadata?: Record<string, unknown>;
}

export interface TrendData {
  label: string;
  value: number;
  previousValue?: number;
  changePercent?: number;
}

export interface DashboardTrends {
  userGrowth: TrendData;
  projectGrowth: TrendData;
  applicationGrowth: TrendData;
  completionRate: TrendData;
}

export interface DashboardOverview {
  users: UserStats;
  projects: ProjectStats;
  assignments: AssignmentStats;
  applications: ApplicationStats;
  revenue: RevenueStats;
  recentActivities: RecentActivity[];
  trends: DashboardTrends;
}

export interface ChartDataPoint {
  date: string;
  value: number;
  label?: string;
}

export interface TopProject {
  _id: string;
  title: string;
  category: string;
  hourlyRate: number;
  totalSlots: number;
  availableSlots: number;
  applicationCount: number;
  completedCount: number;
  activeCount: number;
  utilizationRate: number;
  estimatedRevenue: number;
}

export interface DashboardResponse {
  statusCode: number;
  message: string;
  data: DashboardOverview;
  success: boolean;
}

export interface ChartDataResponse {
  statusCode: number;
  message: string;
  data: ChartDataPoint[];
  success: boolean;
}

export interface ActivitiesResponse {
  statusCode: number;
  message: string;
  data: RecentActivity[];
  success: boolean;
}

export interface TopProjectsResponse {
  statusCode: number;
  message: string;
  data: TopProject[];
  success: boolean;
}

// ============================================================================
// Query Parameters
// ============================================================================

export interface DashboardParams {
  period?: "day" | "week" | "month" | "year";
  startDate?: string;
  endDate?: string;
}

export interface ChartParams extends DashboardParams {
  metric: "users" | "projects" | "applications" | "assignments";
  groupBy?: "day" | "week" | "month";
}

export interface ActivitiesParams {
  limit?: number;
  type?: string;
  resourceType?: string;
}

export interface TopProjectsParams {
  limit?: number;
  sortBy?: "applications" | "completions" | "revenue" | "utilization";
}

// ============================================================================
// API Functions
// ============================================================================

export const getDashboardOverview = async (
  params: DashboardParams = {}
): Promise<DashboardOverview> => {
  console.log("Fetching dashboard with params:", { ...params });

  const response = await api.get<DashboardResponse>("/admin/dashboard", {
    ...params,
  });

  console.log("Raw response:", response);
  console.log("response.data:", response.data);

  const responseData = response.data as any;

  if (responseData.data && responseData.data.users !== undefined) {
    console.log("Returning responseData.data:", responseData.data);
    return responseData.data;
  }

  if (responseData.users !== undefined) {
    console.log("Returning responseData:", responseData);
    return responseData;
  }

  console.log("Returning fallback responseData:", responseData);
  return responseData;
};

export const getChartData = async (
  params: ChartParams
): Promise<ChartDataPoint[]> => {
  const response = await api.get<ChartDataResponse>("/admin/dashboard/charts", {
    ...params,
  });

  const responseData = response.data as any;

  if (responseData.data && Array.isArray(responseData.data)) {
    return responseData.data;
  }

  if (Array.isArray(responseData)) {
    return responseData;
  }

  return [];
};

export const getRecentActivities = async (
  params: ActivitiesParams = {}
): Promise<RecentActivity[]> => {
  const response = await api.get<ActivitiesResponse>(
    "/admin/dashboard/activities",
    { ...params }
  );

  const responseData = response.data as any;

  if (responseData.data && Array.isArray(responseData.data)) {
    return responseData.data;
  }

  if (Array.isArray(responseData)) {
    return responseData;
  }

  return [];
};

export const getTopProjects = async (
  params: TopProjectsParams = {}
): Promise<TopProject[]> => {
  const response = await api.get<TopProjectsResponse>(
    "/admin/dashboard/top-projects",
    { ...params }
  );

  const responseData = response.data as any;

  // If response.data has a nested 'data' property that's an array
  if (responseData.data && Array.isArray(responseData.data)) {
    return responseData.data;
  }

  // If response.data is already an array
  if (Array.isArray(responseData)) {
    return responseData;
  }

  // Fallback
  return [];
};

// ============================================================================
// React Query Hooks
// ============================================================================

export const useGetDashboardOverview = (params: DashboardParams = {}) => {
  return useQuery({
    queryKey: ["dashboard-overview", params],
    queryFn: () => getDashboardOverview(params),
    staleTime: 2 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    retry: 2,
    refetchOnWindowFocus: false,
  });
};

export const useGetChartData = (params: ChartParams) => {
  return useQuery({
    queryKey: ["dashboard-chart", params],
    queryFn: () => getChartData(params),
    staleTime: 2 * 60 * 1000,
    enabled: !!params.metric,
    retry: 2,
    refetchOnWindowFocus: false,
  });
};

export const useGetRecentActivities = (params: ActivitiesParams = {}) => {
  return useQuery({
    queryKey: ["dashboard-activities", params],
    queryFn: () => getRecentActivities(params),
    staleTime: 1 * 60 * 1000,
    retry: 2,
    refetchOnWindowFocus: false,
  });
};

export const useGetTopProjects = (params: TopProjectsParams = {}) => {
  return useQuery({
    queryKey: ["dashboard-top-projects", params],
    queryFn: () => getTopProjects(params),
    staleTime: 2 * 60 * 1000,
    retry: 2,
    refetchOnWindowFocus: false,
  });
};
